import { tokenKey } from '../utils/org-slug'
import { apiClient } from './client'

export interface OrgSettings {
  id: number
  slug: string
  name: string
  logoUrl: string | null
  workspaceDomain: string | null
  isActive: boolean
  createdAt: string
  /** Direct REST API access flag — drives the sidebar API Docs link. */
  restApiEnabled?: boolean
}

/** Full org settings for the org admin (slug is read-only server-side). */
export async function getOrgSettings(): Promise<OrgSettings> {
  return apiClient.get<OrgSettings>('/admin/org')
}

export async function updateOrgSettings(name: string): Promise<OrgSettings> {
  return apiClient.patch<OrgSettings>('/admin/org', { name })
}

/** Branding subset for the sidebar header (any org member may call this). */
export async function getOrgBranding(): Promise<OrgSettings> {
  return apiClient.get<OrgSettings>('/org/current')
}

export async function uploadOrgLogo(file: File): Promise<{ logoUrl: string }> {
  return apiClient.upload<{ logoUrl: string }>('/admin/org/logo', file)
}

export async function deleteOrgLogo(): Promise<{ deleted: boolean }> {
  return apiClient.delete<{ deleted: boolean }>('/admin/org/logo')
}

/**
 * The logo is served by an authenticated endpoint; <img> tags can't send an
 * Authorization header, so the token rides a ?token= query param (accepted by
 * the backend auth hook, mirroring project icons).
 */
export function getAuthenticatedLogoUrl(logoUrl: string | null | undefined): string | null {
  if (!logoUrl) return null
  const token =
    localStorage.getItem(tokenKey('accessToken')) ??
    localStorage.getItem('accessToken') ??
    localStorage.getItem(tokenKey('apiKey')) ??
    localStorage.getItem('apiKey')
  if (!token) return logoUrl
  const separator = logoUrl.includes('?') ? '&' : '?'
  return `${logoUrl}${separator}token=${encodeURIComponent(token)}`
}

/** Fired after the org name/logo changes so the sidebar header refreshes. */
export const ORG_BRANDING_CHANGED_EVENT = 'org-branding-changed'

export function notifyOrgBrandingChanged(): void {
  window.dispatchEvent(new CustomEvent(ORG_BRANDING_CHANGED_EVENT))
}
